import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { apiPost, storage } from "@/lib/api";
import {
  CATEGORIES,
  QUESTIONS,
  QUIZ_PROGRESS_KEY,
  LEAD_SUBMITTED_KEY,
  computeScores,
} from "@/lib/quizConfig";
import { trackStandardOnce } from "@/lib/pixel";
import useNoIndex from "@/lib/useNoIndex";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_connexus-quiz-flow/artifacts/inn9zrlw_Logo.png";

export default function LeadCapture() {
  useNoIndex();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const raw = storage.get(QUIZ_PROGRESS_KEY);
    if (!raw) {
      navigate("/quiz", { replace: true });
      return;
    }
    try {
      const p = JSON.parse(raw);
      const answeredCount = Object.keys(p.answers || {}).length;
      if (answeredCount < QUESTIONS.length) {
        navigate("/quiz", { replace: true });
      }
    } catch {
      navigate("/quiz", { replace: true });
    }
  }, [navigate]);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

  const submit = async () => {
    setError(null);
    if (!firstName.trim()) {
      setError("Please enter your first name.");
      return;
    }
    if (!isEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    const alreadySubmitted = storage.get(LEAD_SUBMITTED_KEY);
    if (alreadySubmitted) {
      navigate(`/processing?result_id=${alreadySubmitted}`, { replace: true });
      return;
    }

    setLoading(true);
    try {
      const raw = storage.get(QUIZ_PROGRESS_KEY);
      const prog = raw ? JSON.parse(raw) : { answers: {} };
      const answers = prog.answers || {};
      const { scores, raw: rawScores, primary, secondary } = computeScores(answers);

      const res = await apiPost("/quiz/submit", {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        answers,
        scores,
        raw_category_scores: rawScores,
        primary_issue: CATEGORIES[primary].title,
        secondary_issue: CATEGORIES[secondary].title,
        origin_url: window.location.origin,
      });
      // Fire Meta Pixel Lead event — once per session, only after successful submit.
      trackStandardOnce("Lead", {
        content_name: "Connexus Quiz Lead",
        content_category: CATEGORIES[primary].title,
      });
      storage.set(LEAD_SUBMITTED_KEY, res.result_id);
      navigate(`/processing?result_id=${res.result_id}`, { replace: true });
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-lg mx-auto px-5 py-6 fade-up">
        <div className="bg-white rounded-3xl border border-connexus-border shadow-connexus-card p-7 sm:p-9">
          <p className="text-[11px] tracking-[0.25em] font-semibold text-connexus mb-2">
            YOU&apos;RE ALMOST THERE
          </p>
          <h1
            className="font-heading text-3xl sm:text-4xl leading-tight tracking-tight text-connexus mb-3"
            data-testid="lead-capture-headline"
          >
            What Does Your Relationship Need Most Right Now?
          </h1>
          <p className="text-connexus-textSecondary text-base leading-relaxed mb-6">
            Enter your details to see your personalized results.
          </p>

          <div className="flex flex-col gap-5">
            <Field label="First name *">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
                autoCapitalize="words"
                disabled={loading}
                className="w-full border-b-2 border-connexus-border bg-transparent py-3 text-lg text-black outline-none focus:border-connexus transition-colors placeholder:text-connexus-textMuted"
                data-testid="lead-first-name-input"
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                autoCapitalize="none"
                disabled={loading}
                className="w-full border-b-2 border-connexus-border bg-transparent py-3 text-lg text-black outline-none focus:border-connexus transition-colors placeholder:text-connexus-textMuted"
                data-testid="lead-email-input"
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
                disabled={loading}
                className="w-full border-b-2 border-connexus-border bg-transparent py-3 text-lg text-black outline-none focus:border-connexus transition-colors placeholder:text-connexus-textMuted"
                data-testid="lead-phone-input"
              />
            </Field>

            {error && (
              <p
                className="text-sm text-connexus-danger"
                data-testid="lead-error"
              >
                {error}
              </p>
            )}

            <button
              onClick={submit}
              disabled={loading}
              className="mt-1 inline-flex items-center justify-center gap-3 bg-connexus hover:bg-connexus-hover text-white rounded-full py-4 font-semibold text-base shadow-connexus-soft transition-all active:scale-[0.98] disabled:opacity-70"
              data-testid="lead-submit-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  See My Results
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <p className="text-[12px] text-connexus-textMuted text-center leading-relaxed mt-1">
              We respect your privacy. We&apos;ll email your results and occasional
              relationship tips. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-semibold text-connexus-textSecondary">
        {label}
      </label>
      {children}
    </div>
  );
}
